import { useEffect, useState } from "react";
import API from "../services/api";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

function AuditLog() {
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [logs, setLogs] = useState([]);
    const [fromDate, setFromDate] = useState("");
    const [toDate, setToDate] = useState("");

    const [sortField, setSortField] = useState("changedOn");
    const [sortOrder, setSortOrder] = useState("desc");

    const [search, setSearch] = useState("");
    const [page, setPage] = useState(1);

    useEffect(() => {
        const today = new Date().toLocaleDateString("en-CA");

        setFromDate(today);
        setToDate(today);

        // ✅ Load ALL logs initially (no filtering issue)
        API.get(`/audit`)
            .then(res => setLogs(res.data));
    }, []);

    const loadLogs = () => {
        let query = [];

        if (fromDate) query.push(`fromDate=${fromDate}`);
        if (toDate) query.push(`toDate=${toDate}`);

        const url = `/audit${query.length ? "?" + query.join("&") : ""}`;

        API.get(url).then(res => setLogs(res.data));
    };

    const handleLast7Days = () => {
        const today = new Date();
        const last7 = new Date();
        last7.setDate(today.getDate() - 7);

        const from = last7.toLocaleDateString("en-CA");
        const to = today.toLocaleDateString("en-CA");

        setFromDate(from);
        setToDate(to);

        API.get(`/audit?fromDate=${from}&toDate=${to}`)
            .then(res => setLogs(res.data));
    };

    const handleSort = (field) => {
        if (sortField === field) {
            setSortOrder(sortOrder === "asc" ? "desc" : "asc");
        } else {
            setSortField(field);
            setSortOrder("asc");
        }
    };

    const parseDate = (dateString) => {
        const d = new Date(dateString);
        return isNaN(d) ? new Date() : d;
    };

    const sortedLogs = [...logs].sort((a, b) => {
        let valA = a[sortField];
        let valB = b[sortField];

        if (sortField === "changedOn") {
            valA = parseDate(valA);
            valB = parseDate(valB);
        } else {
            valA = (valA || "").toLowerCase();
            valB = (valB || "").toLowerCase();
        }

        if (valA < valB) return sortOrder === "asc" ? -1 : 1;
        if (valA > valB) return sortOrder === "asc" ? 1 : -1;
        return 0;
    });

    // ✅ UPDATED SEARCH
    const filteredLogs = sortedLogs.filter(log =>
        log.username?.toLowerCase().includes(search.toLowerCase()) ||
        log.action?.toLowerCase().includes(search.toLowerCase()) ||
        log.auditRemarks?.toLowerCase().includes(search.toLowerCase())
    );

    const indexOfLast = page * rowsPerPage;
    const indexOfFirst = indexOfLast - rowsPerPage;
    const currentLogs = filteredLogs.slice(indexOfFirst, indexOfLast);

    const totalPages = Math.ceil(filteredLogs.length / rowsPerPage);

    const formatDate = (dateString) => {
        const date = new Date(dateString);

        const day = String(date.getDate()).padStart(2, "0");
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const year = date.getFullYear();

        let hours = date.getHours();
        const minutes = String(date.getMinutes()).padStart(2, "0");
        const seconds = String(date.getSeconds()).padStart(2, "0");

        const ampm = hours >= 12 ? "PM" : "AM";
        hours = hours % 12 || 12;

        return `${day}/${month}/${year} ${String(hours).padStart(2, "0")}:${minutes}:${seconds} ${ampm}`;
    };

    // ✅ UPDATED EXPORT
    const exportToExcel = () => {
        const exportData = filteredLogs.map(log => ({
            Date: formatDate(log.changedOn),
            User: log.username,
            Action: log.action,
            Details: log.auditRemarks
        }));

        const ws = XLSX.utils.json_to_sheet(exportData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "AuditLogs");

        const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
        const blob = new Blob([excelBuffer], { type: "application/octet-stream" });

        saveAs(blob, "AuditLogs.xlsx");
    };

    return (
        <div style={styles.container}>
            <div style={styles.header}>
                <h2>Audit Logs</h2>
            </div>

            <div style={styles.filterBar}>
                <div>
                    <label>From:</label>
                    <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} style={styles.dateInput} />
                </div>

                <div>
                    <label>To:</label>
                    <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} style={styles.dateInput} />
                </div>

                <button style={styles.filterBtn} onClick={loadLogs}>Filter</button>
                <button style={styles.filterBtn} onClick={handleLast7Days}>Last 7 Days</button>

                <button style={styles.resetBtn} onClick={() => {
                    setFromDate("");
                    setToDate("");
                    loadLogs();
                }}>
                    Reset
                </button>

                <input
                    type="text"
                    placeholder="Search..."
                    value={search}
                    onChange={(e) => {
                        setSearch(e.target.value);
                        setPage(1);
                    }}
                    style={styles.searchInput}
                />

                <button style={styles.exportBtn} onClick={exportToExcel}>
                    Export
                </button>

                <span style={{ marginLeft: "12px", fontFamily: "Verdana", fontSize: "12px" }}>
                    Rows per page:
                </span>

                <select
                    value={rowsPerPage}
                    onChange={(e) => {
                        setRowsPerPage(Number(e.target.value));
                        setPage(1);
                    }}
                    style={{
                        padding: "5px",
                        fontFamily: "Verdana",
                        fontSize: "12px",
                        borderRadius: "4px",
                        marginLeft: "10px"
                    }}
                >
                    <option value="5">05</option>
                    <option value={10}>10</option>
                    <option value={20}>20</option>
                    <option value={30}>30</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                </select>
            </div>

            <table style={styles.table}>
                <thead>
                    <tr>
                        <th style={styles.th} onClick={() => handleSort("changedOn")}>
                            Date {sortField === "changedOn" ? (sortOrder === "asc" ? "↑" : "↓") : ""}
                        </th>
                        <th style={styles.th} onClick={() => handleSort("username")}>
                            User {sortField === "username" ? (sortOrder === "asc" ? "↑" : "↓") : ""}
                        </th>
                        <th style={styles.th} onClick={() => handleSort("action")}>
                            Action {sortField === "action" ? (sortOrder === "asc" ? "↑" : "↓") : ""}
                        </th>
                        <th style={styles.th}>Details</th>
                    </tr>
                </thead>

                <tbody>
                    {filteredLogs.length === 0 ? (
                        <tr>
                            <td colSpan="4" style={{ textAlign: "center", padding: "10px" }}>
                                No audit records found
                            </td>
                        </tr>
                    ) : (
                        currentLogs.map((log) => (
                            <tr key={log.id}>
                                <td style={styles.td}>{formatDate(log.changedOn)}</td>
                                <td style={styles.td}>{log.username}</td>
                                <td style={styles.td}>{log.action}</td>
                                <td style={styles.td}>{log.auditRemarks}</td>
                            </tr>
                        ))
                    )}
                </tbody>
            </table>

            <div style={styles.pagination}>
                <button disabled={page === 1} onClick={() => setPage(page - 1)}>Prev</button>
                <span> Page {page} of {totalPages} </span>
                <button disabled={page === totalPages} onClick={() => setPage(page + 1)}>Next</button>
            </div>
        </div>
    );
}

const styles = {
    container: { padding: "12px", fontFamily: "Verdana", fontSize: "12px" },
    filterBar: { display: "flex", gap: "10px", alignItems: "center", marginBottom: "12px", flexWrap: "wrap" },
    dateInput: { marginLeft: "5px", padding: "5px", fontSize: "12px" },
    searchInput: { padding: "5px", border: "1px solid #ccc", borderRadius: "4px" },
    filterBtn: { background: "#2ed573", border: "none", padding: "6px 12px", color: "#fff", borderRadius: "4px", cursor: "pointer" },
    resetBtn: { background: "#ff4757", border: "none", padding: "6px 12px", color: "#fff", borderRadius: "4px", cursor: "pointer" },
    exportBtn: { background: "#1e90ff", border: "none", padding: "6px 12px", color: "#fff", borderRadius: "4px", cursor: "pointer" },
    table: { width: "100%", borderCollapse: "collapse" },
    th: { border: "1px solid #ccc", padding: "8px", background: "#2f3542", color: "#fff", cursor: "pointer" },
    td: { border: "1px solid #ddd", padding: "8px" },
    pagination: { marginTop: "12px", display: "flex", justifyContent: "center", gap: "10px" },
    header: {
        background: "#2f3542",
        color: "#fff",
        padding: "6px",
        borderRadius: "6px",
        marginBottom: "10px"
    },
};

export default AuditLog;